import ClickableWrapper from "@shared/designSystem/ClickableWrapper";
import { HollowedButton } from "@shared/designSystem/Button";
import { ColumnsContainer, HorizontalContainer, VerticalContainer } from "@shared/designSystem/Containers";
import DetailField from "@shared/designSystem/DetailField";
import { Modal } from "@shared/designSystem/messages/Modal";
import { GridTable, GridTableCell, GridTableRow, TableColumn } from "@shared/designSystem/Tables";
import { BoldText, SmallText, Text } from "@shared/designSystem/Texts";
import { SystemIcon } from "@shared/designSystem/SystemIcon";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { NovedadesAuditChange, NovedadesAuditLog } from "@services/NovedadesService";
import { useConfiguracionNovedadesAuditoriaViewModel } from "./useConfiguracionNovedadesAuditoriaViewModel";

interface ConfiguracionNovedadesAuditoriaProps {
    orgID: string;
}

const columns: TableColumn[] = [
    { id: "fecha", label: <Text text={"**Fecha**".localized()} markdown={true} />, width: "18%" },
    { id: "hora", label: <Text text={"**Hora**".localized()} markdown={true} />, width: "14%" },
    { id: "tipo", label: <Text text={"**Tipo**".localized()} markdown={true} />, width: "28%" },
    { id: "responsable", label: <Text text={"**Responsable**".localized()} markdown={true} />, width: "28%" },
    { id: "detalle", label: <Text text={"**Detalle**".localized()} markdown={true} />, width: "12%", align: "center" },
];

const fieldLabelMap: Record<string, string> = {
    monday: "Lunes",
    tuesday: "Martes",
    wednesday: "Miércoles",
    thursday: "Jueves",
    friday: "Viernes",
    saturday: "Sábado",
    sunday: "Domingo",
    dates: "Feriados",
    name: "Nombre",
    acronym: "Código",
    payrollCode: "Código de sistema liquidación",
    backgroundColor: "Color de fondo",
    foregroundColor: "Color de texto",
    isUsableInLicenses: "Habilitado para WhatsApp",
    disabled: "Estado",
};

const DetailSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <VerticalContainer width="100%" gap={designSystemStyles.gapMd}>
        <Text text={`**${title.trim()}**`} color={designSystemStyles.colorText} markdown={true} />
        <VerticalContainer
            width="100%"
            gap={designSystemStyles.gapMd}
            backgroundColor={designSystemStyles.colorBackgroundGray2}
            borderRadius={designSystemStyles.cornerRadiusComponent}
            padding={designSystemStyles.paddingMd}
        >
            {children}
        </VerticalContainer>
    </VerticalContainer>
);

const EmptyState = ({ text }: { text: string }) => (
    <VerticalContainer
        height="120px"
        alignment="center"
        justifyContent="center"
        backgroundColor={designSystemStyles.colorBackgroundWhite}
        borderRadius={designSystemStyles.cornerRadiusComponent}
    >
        <Text text={text} color={designSystemStyles.colorTextSecondary} alignment="center" />
    </VerticalContainer>
);

const AuditChangeField = ({
    label,
    value,
}: {
    label: string;
    value: string;
}) => (
    <VerticalContainer width="100%" gap={designSystemStyles.gapSm}>
        <SmallText text={label} color={designSystemStyles.colorText} />
        <BoldText text={value} size="sm"/>
    </VerticalContainer>
);

function isEmptyAuditValue(value: unknown): boolean {
    if (value === undefined || value === null) {
        return true;
    }

    if (typeof value === "string") {
        return value.trim() === "";
    }

    const normalizedList = normalizeValueList(value);
    if (normalizedList) {
        return normalizedList.length === 0;
    }

    return false;
}

function normalizeValueList(value: unknown): string[] | null {
    if (Array.isArray(value)) {
        return value.map((item) => String(item));
    }

    if (!value || typeof value !== "object") {
        return null;
    }

    const entries = Object.entries(value as Record<string, unknown>);
    const isIndexedObject = entries.every(([key]) => /^\d+$/.test(key));

    if (!isIndexedObject) {
        return null;
    }

    return entries
        .sort((left, right) => Number(left[0]) - Number(right[0]))
        .map(([, item]) => String(item));
}

function formatDateString(value: string): string {
    const [year, month, day] = value.split("-");
    if (!year || !month || !day) {
        return value;
    }
    return `${day}/${month}/${year}`;
}

function formatAuditFieldLabel(field: string): string {
    return (fieldLabelMap[field] ?? field).localized();
}

function formatAuditValue(field: string, value: unknown): string {
    if (field === "disabled") {
        return value ? "Deshabilitada".localized() : "Activa".localized();
    }

    if (field === "isUsableInLicenses") {
        return value ? "Habilitado".localized() : "Deshabilitado".localized();
    }
    if (value === undefined || value === null) {
        return "Sin valor".localized();
    }

    if (["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].includes(field)) {
        return value ? "Activo".localized() : "Inactivo".localized();
    }

    const normalizedList = normalizeValueList(value);

    if (field === "dates" && normalizedList) {
        if (!normalizedList.length) {
            return "Sin feriados".localized();
        }
        return normalizedList.map((date) => formatDateString(date)).join(", ");
    }

    if (normalizedList) {
        return normalizedList.join(", ");
    }

    if (typeof value === "boolean") {
        return value ? "Sí".localized() : "No".localized();
    }

    if (typeof value === "object") {
        try {
            return JSON.stringify(value);
        } catch {
            return "Valor no disponible".localized();
        }
    }

    return String(value);
}

const GenericAuditDiff = ({
    field,
    previousValue,
    newValue,
    action,
}: {
    field: string;
    previousValue: unknown;
    newValue: unknown;
    action: string;
}) => {
    const isCreateAction = action === "NOVEDAD_CREATED";
    const isDisableAction = action === "NOVEDAD_DISABLED";
    const hasPreviousValue = !isEmptyAuditValue(previousValue);
    const hasNewValue = !isEmptyAuditValue(newValue);

    if (isCreateAction && !hasPreviousValue && hasNewValue) {
        return (
            <AuditChangeField
                label={"Valor creado".localized()}
                value={formatAuditValue(field, newValue)}
            />
        );
    }

    return (
        <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
            <AuditChangeField
                label={isDisableAction ? "Estado anterior".localized() : "Valor anterior".localized()}
                value={formatAuditValue(field, previousValue)}
            />
            <AuditChangeField
                label={isDisableAction ? "Estado actual".localized() : "Valor nuevo".localized()}
                value={formatAuditValue(field, newValue)}
            />
        </ColumnsContainer>
    );
};

const DatesAuditDiff = ({
    previousValue,
    newValue,
}: {
    previousValue: unknown;
    newValue: unknown;
}) => {
    const previousDates = normalizeValueList(previousValue) ?? [];
    const currentDates = normalizeValueList(newValue) ?? [];

    return (
        <VerticalContainer width="100%" gap={designSystemStyles.gapMd}>
            <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                <AuditChangeField
                    label={"Fechas anteriores".localized()}
                    value={formatAuditValue("dates", previousDates)}
                />
                <AuditChangeField
                    label={"Fechas actualizadas".localized()}
                    value={formatAuditValue("dates", currentDates)}
                />
            </ColumnsContainer>
        </VerticalContainer>
    );
};

function getEntityLabel(auditLog: NovedadesAuditLog): string {
    if (auditLog.entityID) {
        return auditLog.entityID;
    }

    switch (auditLog.section) {
        case "working-days":
            return "Días laborables".localized();
        case "holidays":
            return "Feriados".localized();
        case "catalog":
            return "Catálogo".localized();
        default:
            return "Configuración".localized();
    }
}

const AuditDetailModal = ({
    auditLog,
    onClose,
}: {
    auditLog: NovedadesAuditLog | null;
    onClose: () => void;
}) => (
    <Modal
        isOpen={auditLog != null}
        onClose={onClose}
        title={"Detalle de auditoria".localized()}
        width="720px"
        maxWidth="90vw"
    >
        {auditLog && (
            <VerticalContainer width="100%" gap={designSystemStyles.gapLg} alignment="left">
                <DetailSection title={"Resumen".localized()}>
                    <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                        <DetailField label={"Tipo".localized()} value={auditLog.tipo.localized()} />
                        <DetailField label={"Responsable".localized()} value={auditLog.responsable} />
                        <DetailField label={"Fecha".localized()} value={auditLog.fecha} />
                        <DetailField label={"Hora".localized()} value={auditLog.hora} />
                        <DetailField label={"Sección".localized()} value={getEntityLabel(auditLog)} />
                    </ColumnsContainer>
                </DetailSection>

                <DetailSection title={"Cambios detectados".localized()}>
                    {auditLog.changes.length ? (
                        <VerticalContainer width="100%" gap={designSystemStyles.gapMd}>
                            {auditLog.changes.map((change: NovedadesAuditChange, index: number) => (
                                <VerticalContainer
                                    key={`${change.field}-${index}`}
                                    width="100%"
                                    gap={designSystemStyles.gapMd}
                                >
                                    <Text text={formatAuditFieldLabel(change.field)} />
                                    {change.field === "dates" ? (
                                        <DatesAuditDiff
                                            previousValue={change.oldValue}
                                            newValue={change.newValue}
                                        />
                                    ) : (
                                        <GenericAuditDiff
                                            field={change.field}
                                            previousValue={change.oldValue}
                                            newValue={change.newValue}
                                            action={auditLog.action}
                                        />
                                    )}
                                </VerticalContainer>
                            ))}
                        </VerticalContainer>
                    ) : (
                        <Text text={"Todavía no hay detalle del cambio disponible para este registro.".localized()} />
                    )}
                </DetailSection>

                <HorizontalContainer justifyContent="flex-end" width="100%">
                    <HollowedButton
                        text={"Cerrar".localized()}
                        onClick={onClose}
                    />
                </HorizontalContainer>
            </VerticalContainer>
        )}
    </Modal>
);

export default function ConfiguracionNovedadesAuditoria({
    orgID,
}: ConfiguracionNovedadesAuditoriaProps) {
    const viewModel = useConfiguracionNovedadesAuditoriaViewModel({ orgID });

    return (
        <VerticalContainer
            height="100%"
            gap={designSystemStyles.gapLg}
            margin={designSystemStyles.paddingZero}
        >
            <HorizontalContainer gap={designSystemStyles.gapSm}>
                <SystemIcon type="notes" color={designSystemStyles.colorTextSecondary} />
                <BoldText text={"Auditoria".localized()} size="lg" />
            </HorizontalContainer>

            {viewModel.error && (
                <SmallText text={viewModel.error} color={designSystemStyles.colorError} />
            )}

            <GridTable columns={columns} minWidth="760px" isLoading={viewModel.loading}>
                {viewModel.auditData.length ? (
                    viewModel.auditData.map((row) => (
                        <GridTableRow key={row.id}>
                            <GridTableCell>
                                <Text text={row.fecha} truncate={true} />
                            </GridTableCell>
                            <GridTableCell>
                                <Text text={row.hora} truncate={true} />
                            </GridTableCell>
                            <GridTableCell>
                                <Text text={row.tipo.localized()} truncate={true} />
                            </GridTableCell>
                            <GridTableCell>
                                <Text text={row.responsable} truncate={true} />
                            </GridTableCell>
                            <GridTableCell align="center">
                                <ClickableWrapper
                                    onClick={() => viewModel.openDetail(row)}
                                    title={"Ver detalle".localized()}
                                    style={{
                                        width: "36px",
                                        height: "36px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <SystemIcon type="eye" color={designSystemStyles.colorMainAction} />
                                </ClickableWrapper>
                            </GridTableCell>
                        </GridTableRow>
                    ))
                ) : (
                    <EmptyState
                        text={
                            viewModel.error
                                ? "No se pudo cargar la auditoría de novedades.".localized()
                                : "No hay registros de auditoria para la configuración de novedades.".localized()
                        }
                    />
                )}
            </GridTable>

            <AuditDetailModal
                auditLog={viewModel.selectedAuditLog}
                onClose={viewModel.closeDetail}
            />
        </VerticalContainer>
    );
}
